import type { Session, User } from '@supabase/supabase-js';
import { useSyncExternalStore } from 'react';

import { supabase } from '@/lib/supabase';

interface SupabaseHookState {
  session: Session | null;
  user: User | null;
  loading: boolean;
}

/**
 * Estado de sesión de Supabase, ÚNICO para toda la app.
 *
 * 🔴 Antes cada llamada al hook montaba su propio `useState` + su propia
 * `getSession()` + su propia suscripción a `onAuthStateChange`. Con 73 archivos
 * consumiéndolo, una pantalla cualquiera disparaba decenas de `getSession()` en
 * paralelo — y `getSession()` toma un lock del navegador sobre el token, así
 * que se serializan. Los que todavía no habían resuelto tenían `session: null`,
 * y como `usePermissions` mira los claims del token y NO mira `loading`, esos
 * componentes concluían «este usuario no tiene ningún permiso»: la app quedaba
 * mostrando «No tenés acceso a esta sección» y una barra lateral de tres
 * entradas, con la sesión de superadmin intacta en el token. Se recomponía sola
 * recargando, que es la firma de una carrera.
 *
 * Ahora hay UNA sola lectura inicial y UNA sola suscripción, compartidas por
 * todos los consumidores vía `useSyncExternalStore`: o todos ven la sesión o
 * ninguno, nunca la mitad. La firma del hook no cambió.
 */
interface Estado {
  session: Session | null;
  loading: boolean;
}

let estado: Estado = { session: null, loading: true };
const oyentes = new Set<() => void>();
let arrancado = false;

function publicar(next: Estado) {
  // La identidad del objeto es lo que compara `useSyncExternalStore`: solo se
  // reemplaza cuando algo cambió de verdad, si no cada aviso re-renderiza los
  // 73 consumidores.
  if (next.session === estado.session && next.loading === estado.loading) return;
  estado = next;
  oyentes.forEach((f) => f());
}

function arrancar() {
  if (arrancado) return;
  arrancado = true;

  void supabase.auth.getSession().then(({ data }) => {
    publicar({ session: data.session, loading: false });
  });

  supabase.auth.onAuthStateChange((_event, nextSession) => {
    // Un evento de auth también cierra la carga: si llega antes que
    // `getSession()`, la sesión ya está resuelta y no hay nada que esperar.
    publicar({ session: nextSession, loading: false });
  });
}

function subscribe(onStoreChange: () => void): () => void {
  arrancar();
  oyentes.add(onStoreChange);
  return () => {
    oyentes.delete(onStoreChange);
  };
}

function getSnapshot(): Estado {
  return estado;
}

/**
 * Hook que expone el estado de sesión de Supabase.
 *
 * Para acciones (sign in/out) usar `supabase.auth.*` directamente desde el
 * componente; este hook es solo lectura del estado.
 */
export function useSupabase(): SupabaseHookState {
  const { session, loading } = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  return {
    session,
    user: session?.user ?? null,
    loading,
  };
}
